import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";

const AdminComplaints = () => {
  const theme = useTheme();
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [complaints, setComplaints] = useState([]);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchComplaints = async () => {
    setIsLoading(true);
    const loadingToast = toast.loading("Fetching complaints...");
    try {
      const token = localStorage.getItem("token");
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}complaints/`,
        config
      );
      setComplaints(res.data);
      toast.dismiss(loadingToast);
      setIsLoading(false);
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("Failed to fetch complaints");
      setIsLoading(false);
    }
  };

  const handleMarkResolved = async (id) => {
    const loadingToast = toast.loading("Marking as resolved...");
    try {
      const token = localStorage.getItem("token");
      // Find the complaint by ID if selectedComplaint is not set
      const complaintToResolve =
        selectedComplaint || complaints.find((c) => c.id === id);

      await axios.post(
        `${process.env.REACT_APP_API_URL}complaints/${id}/resolve/`,
        {
          title: complaintToResolve.title,
          description: complaintToResolve.description,
          category: complaintToResolve.category,
          status: complaintToResolve.status,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      toast.dismiss(loadingToast);
      toast.success("Complaint marked as resolved");
      setSelectedComplaint(null);
      fetchComplaints();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("Failed to update complaint");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "resolved":
        return "bg-green-100 text-green-800 border-green-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };
    return date.toLocaleString("en-IN", options);
  };

  const ComplaintCard = ({ complaint }) => (
    <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200 hover:shadow-lg transition-shadow duration-200">
      <div className="space-y-3">
        {/* Header with Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ReportProblemIcon className="text-red-600 text-lg" />
            <span className="font-semibold text-gray-900">
              {complaint.title}
            </span>
          </div>
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
              complaint.status
            )}`}
          >
            {complaint.status?.toUpperCase()}
          </span>
        </div>

        {/* Complaint Details */}
        <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-gray-500">ID:</span>
              <span className="font-mono text-gray-900 ml-1">
                {complaint.id}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Category:</span>
              <span className="text-gray-900 ml-1 capitalize">
                {complaint.category}
              </span>
            </div>
          </div>
          <div>
            <span className="text-gray-500">Student:</span>
            <span className="font-medium text-gray-900 ml-1">
              {complaint.student_name}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Description:</span>
            <span className="text-gray-900 ml-1 line-clamp-2">
              {complaint.description}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Submitted:</span>
            <span className="text-gray-900 ml-1 text-sm">
              {formatDateTime(complaint.submitted_on)}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
          <button
            onClick={() => setSelectedComplaint(complaint)}
            className="bg-blue-600 text-white px-3 py-2 rounded-lg text-xs hover:bg-blue-700 transition-colors duration-200 shadow-sm flex-1 flex items-center justify-center gap-1"
          >
            <VisibilityIcon className="text-sm" />
            View Details
          </button>
          {complaint.status !== "resolved" && (
            <button
              onClick={() => {
                setSelectedComplaint(complaint);
                handleMarkResolved(complaint.id);
              }}
              className="bg-green-600 text-white px-3 py-2 rounded-lg text-xs hover:bg-green-700 transition-colors duration-200 shadow-sm flex-1 flex items-center justify-center gap-1"
            >
              <CheckCircleIcon className="text-sm" />
              Resolve
            </button>
          )}
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    fetchComplaints();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-6">
      <div className="max-w-full mx-auto">
        {/* Refresh Button */}
        <div className="flex justify-end mb-4">
          <button
            onClick={fetchComplaints}
            disabled={isLoading}
            className="flex items-center gap-2 bg-white text-gray-600 p-2 rounded-lg hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            title="Refresh complaints"
          >
            <RefreshIcon
              className={`text-sm ${isLoading ? "animate-spin" : ""}`}
            />
          </button>
        </div>

        {/* Mobile Cards View */}
        <div className="block md:hidden">
          {complaints.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <div className="flex flex-col items-center gap-2">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                  <ReportProblemIcon className="text-gray-400 text-2xl" />
                </div>
                <p className="text-gray-500 font-medium">No complaints found</p>
                <p className="text-gray-400 text-sm">
                  All complaints have been resolved or no new complaints exist
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {complaints.map((complaint, index) => (
                <ComplaintCard key={index} complaint={complaint} />
              ))}
            </div>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gradient-to-r from-red-600 to-red-700 text-white">
                <tr>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    ID
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Student Name
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Title
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Description
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Category
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Submitted On
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Status
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {complaints.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                          <ReportProblemIcon className="text-gray-400 text-2xl" />
                        </div>
                        <p className="text-gray-500 font-medium">
                          No complaints found
                        </p>
                        <p className="text-gray-400 text-sm">
                          All complaints have been resolved or no new complaints
                          exist
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  complaints.map((complaint) => (
                    <tr
                      key={complaint.id}
                      className="hover:bg-gray-50 transition-colors duration-200"
                    >
                      <td className="px-4 py-4 font-mono text-gray-600">
                        {complaint.id}
                      </td>
                      <td className="px-4 py-4">
                        <div className="font-medium text-gray-900">
                          {complaint.student_name}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-gray-900 font-medium">
                        {complaint.title}
                      </td>
                      <td className="px-4 py-4 text-gray-600 max-w-xs truncate">
                        {complaint.description}
                      </td>
                      <td className="px-4 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full font-medium bg-blue-100 text-blue-800 capitalize">
                          {complaint.category}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-gray-600 text-sm">
                        {formatDateTime(complaint.submitted_on)}
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                            complaint.status
                          )}`}
                        >
                          {complaint.status?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() => setSelectedComplaint(complaint)}
                            className="bg-blue-600 text-white px-3 py-2 rounded-lg text-xs hover:bg-blue-700 transition-colors duration-200 shadow-sm flex items-center gap-1"
                          >
                            <VisibilityIcon className="text-sm" />
                            View
                          </button>
                          {complaint.status !== "resolved" && (
                            <button
                              onClick={() => handleMarkResolved(complaint.id)}
                              className="bg-green-600 text-white px-3 py-2 rounded-lg text-xs hover:bg-green-700 transition-colors duration-200 shadow-sm flex items-center gap-1"
                            >
                              <CheckCircleIcon className="text-sm" />
                              Resolve
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* View Modal */}
      {selectedComplaint && (
        <Dialog
          sx={{ zIndex: 50 }}
          open={true}
          onClose={() => setSelectedComplaint(null)}
          fullWidth
          maxWidth={isSmallMobile ? "xs" : "sm"}
          PaperProps={{
            sx: {
              borderRadius: "16px",
              margin: isSmallMobile ? "16px" : "32px",
            },
          }}
        >
          <DialogTitle className="bg-gradient-to-r from-red-600 to-red-700 text-white p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <ReportProblemIcon className="text-white" />
              </div>
              <Typography variant="h6" className="font-bold">
                Complaint Details
              </Typography>
            </div>
          </DialogTitle>
          <DialogContent className="p-6 space-y-4 mt-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500 font-medium">ID:</span>
                <span className="font-mono text-gray-900 ml-2">
                  {selectedComplaint.id}
                </span>
              </div>
              <div>
                <span className="text-gray-500 font-medium">Category:</span>
                <span className="text-gray-900 ml-2 capitalize">
                  {selectedComplaint.category}
                </span>
              </div>
            </div>

            <div>
              <span className="text-gray-500 font-medium">Student Name:</span>
              <span className="text-gray-900 ml-2 font-medium">
                {selectedComplaint.student_name}
              </span>
            </div>

            <div>
              <span className="text-gray-500 font-medium">Title:</span>
              <span className="text-gray-900 ml-2 font-medium">
                {selectedComplaint.title}
              </span>
            </div>

            <div>
              <span className="text-gray-500 font-medium">Description:</span>
              <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-900">
                  {selectedComplaint.description}
                </span>
              </div>
            </div>

            <div>
              <span className="text-gray-500 font-medium">Submitted By:</span>
              <span className="text-gray-900 ml-2">
                {selectedComplaint.submitted_by}
              </span>
            </div>

            <div>
              <span className="text-gray-500 font-medium">Submitted On:</span>
              <span className="text-gray-900 ml-2">
                {formatDateTime(selectedComplaint.submitted_on)}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-gray-500 font-medium">Status:</span>
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                  selectedComplaint.status
                )}`}
              >
                {selectedComplaint.status?.toUpperCase()}
              </span>
            </div>

            {selectedComplaint.screenshot && (
              <div>
                <span className="text-gray-500 font-medium">Photo:</span>
                <div className="mt-2">
                  <img
                    src={selectedComplaint.screenshot}
                    onClick={() =>
                      setPreviewImage(selectedComplaint.screenshot)
                    }
                    alt="Complaint"
                    className="w-full rounded-lg shadow-md cursor-pointer hover:shadow-lg transition-shadow duration-200"
                  />
                </div>
              </div>
            )}
          </DialogContent>
          <DialogActions className="p-6 pt-0 gap-3">
            {selectedComplaint?.status !== "resolved" && (
              <Button
                variant="contained"
                color="success"
                onClick={() => handleMarkResolved(selectedComplaint.id)}
                className="bg-green-600 hover:bg-green-700"
                sx={{ borderRadius: "12px" }}
                startIcon={<CheckCircleIcon />}
              >
                Mark as Resolved
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={() => setSelectedComplaint(null)}
              sx={{
                borderRadius: "12px",
                borderColor: "#d1d5db",
                color: "#374151",
                "&:hover": {
                  borderColor: "#9ca3af",
                  backgroundColor: "#f9fafb",
                },
              }}
              startIcon={<CloseIcon />}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 cursor-zoom-out"
          onClick={() => setPreviewImage(null)}
        >
          <img
            src={previewImage}
            alt="Preview"
            className="max-w-[90%] max-h-[90%] rounded-lg shadow-2xl"
          />
        </div>
      )}
    </div>
  );
};

export default AdminComplaints;
